import { Box, Button, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material"
import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { selectUserInfo } from "features/user/userSlice";
import { useSelector } from "react-redux";
import AlertMessages from "common/Alert/AlertMessages";
import { useNavigate, useParams } from "react-router-dom";
import { InventoryProfileService } from "subModule/src/core/services";
import { NewPurchaseOrderResponse, PurchaseOrderItems, PurchaseOrderResponse } from "subModule/src/services/inventory/interface";
import Loader from "layout/Loader";
import { useTranslation } from "react-i18next";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
const PrintPurchaseOrder: React.FC = () => {
    const params = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const branding = useSelector(brandingInfo);
    const themeColors = branding?.attributes?.themeId === 100 ? branding?.attributes?.customTheme : (themes as any)[branding?.attributes?.themeId || 3];
    const loggedInUserCurrency = useSelector(selectUserInfo)?.business?.currency;
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState<boolean>(true);
    const [po, setPO] = useState<PurchaseOrderResponse>();

    useEffect(() => {
        if (loading) getPOById();
    }, []);
    const getPOById = () => {
        InventoryProfileService.getPurchaseOrderById(params?.id).then((res: NewPurchaseOrderResponse) => {
            if (res.success) {
                setPO((res.data as any));
                setLoading(false);
            } else {
                setLoading(false);
                enqueueSnackbar(<AlertMessages text={res.message || "Something went wrong"} />, { variant: 'error' });
            }
        }).catch((err: any) => { console.error("===", err); });
    }
    const columns = [
        { id: 'itemId' },
        { id: 'itemCode'},
        { id: 'unitId' },
        { id: 'itemName', label: t('Item'), numeric: false, disablePadding: false, type: 'text' },
        { id: 'supplierSKU', label: t('SKU'), numeric: false, disablePadding: false, type: 'text' },
        { id: 'quantity', label: t('Quantity'), numeric: false, disablePadding: false, type: 'number' },
        { id: 'unitTitle', label: t('unit') },
        { id: 'unitPrice', label: t('Cost'), numeric: false, disablePadding: false, type: 'number' },
        { id: 'discount', label: `${t('Discount')} %`, numeric: false, disablePadding: false, type: 'number' },
        { id: 'total', label: t('Total') },
    ];
    const columnsService = [
        { id: 'serviceId' },
        { id: 'serviceCode'},
        { id: 'serviceName', label: t('service'), numeric: false, disablePadding: false, type: 'text' },
        { id: 'quantity', label: t('Quantity'), numeric: false, disablePadding: false, type: 'number' },
        { id: 'unitPrice', label: t('Cost'), numeric: false, disablePadding: false, type: 'number' },
        { id: 'discount', label: `${t('Discount')} %`, numeric: false, disablePadding: false, type: 'number' },
        { id: 'total', label: t('Total') },
    ];
    return (
        loading ? <Loader /> : <Box>
            <Box className='max-w-[1100px] bg-white py-[10px] px-[20px] rounded-[8px] block mx-auto hide-on-print mb-4'>
                <Box className='flex justify-between items-center' id='form'>
                    <Typography className="font-Saira text-[12px] font-semibold" style={{ color: themeColors?.primary }}>{t('Purchase Order')}</Typography>
                    <Box className='flex gap-2'>
                        <Button className="secondary" variant="contained" style={{ color: themeColors?.primary }} disableElevation onClick={() => navigate(-1)}>{t('back')}</Button>
                        <Button className="primary" variant="contained" style={{ background: themeColors?.primary }} disableElevation onClick={() => window.print()}>{t('Print')}</Button>
                    </Box>
                </Box>
            </Box>
            <Box id="print-root" className='max-w-[1100px] bg-white p-[20px] rounded-[8px] block mx-auto'>
                <Box className='flex justify-between mb-2 relative'>
                    <Typography className="font-Saira text-[12px] text-left">
                        <span className="italic font-medium">{t('Supplier')} {t('name')}: </span>
                        <span className="font-bold">{po?.vendorTitle || ''}</span>
                    </Typography>
                    {po?.urgencyStatus ? <Box 
                        className={`absolute left-1/2 -translate-x-[70%] text-center font-medium text-[12px] font-Saira`}
                    >
                        <span className="italic">{t('Priority') + ':'}</span>{' '}
                        <span className={`px-3 py-1 text-[13px] rounded-lg ${po?.urgencyStatus === 10 ? "text-green-500 status-active" 
                                : po?.urgencyStatus === 20 ? "text-orange-500 status-inactive" 
                                : po?.urgencyStatus === 30 ? "text-red-500 status-cancelled" 
                                : ""}`}>
                                    {po?.urgencyStatus === 10 ? t("Low")
                                        : po?.urgencyStatus === 20 ? t("Medium")
                                        : po?.urgencyStatus === 30 ? t("High")
                                        : ""}
                        </span>
                    </Box> : null}
                    <Box>
                        <Box className='block py-1 px-2 rounded-[8px]' style={{ background: themeColors?.appBg }}>
                            <Typography className="font-Saira text-[12px]">
                                <span className="font-bold" style={{ color: themeColors?.secondary }}>{t('Purchase Order')} # </span>
                                <span className="font-semibold">{po?.poNumber || ''}</span>
                            </Typography>
                        </Box>
                        <Typography className="font-Saira text-[12px] text-end font-semibold">
                            <span className="italic font-medium">{t('Date')}: </span>
                            <span>{po?.poDate?.split('T')[0] || ''}</span>
                        </Typography>
                    </Box>
                </Box>
                <Divider className="bg-[#AEB3C4] mb-2" />
                <Typography className="font-Saira text-[12px] font-semibold text-[#333843] text-left mb-2">{t('Purchase Order')}</Typography>
                <Box className='w-[100%] mb-2' id="inventorytable">
                    <TableContainer>
                        <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size={'small'}>
                            <TableHead>
                                <TableRow sx={{ borderBottom: `1px solid #B8BDCB` }}>
                                    {columns.filter((column) => column.id !== 'itemId' && column.id !== 'itemCode' && column.id !== 'unitId').map((column) => (
                                        <TableCell
                                            key={column.id}
                                            align={column.numeric ? 'right' : 'left'}
                                            padding={column.disablePadding ? 'none' : 'normal'}
                                            sortDirection={false}
                                            className="font-bold text-center"
                                        >{column.label}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody sx={{ '& tr:nth-child(odd)': { background: `transparent !important` }, '& tr': { borderBottom: `1px solid #B8BDCB` }, '& tr:last-child': { borderBottom: 0 } }}>
                                {(po?.purchaseOrderItems || []).map((row: PurchaseOrderItems, index: number) => {
                                    const labelId = `enhanced-table-checkbox-${index}`;
                                    return (
                                        <TableRow hover tabIndex={-1} key={row.id} sx={{ height: '36px' }}>
                                            {columns.filter((column) => column.id !== 'itemId' && column.id !== 'itemCode' && column.id !== 'unitId').map((column) => (
                                                <TableCell
                                                    key={column.id}
                                                    align={column.numeric ? 'right' : 'left'}
                                                    padding={column.disablePadding ? 'none' : 'normal'}
                                                    component={column.id === 'itemName' ? 'th' : undefined}
                                                    id={column.id === 'itemName' ? labelId : undefined}
                                                    scope={column.id === 'itemName' ? 'row' : undefined}
                                                    className="text-center"
                                                >{column.id === 'unitTitle' ? ((row as any)[column.id] || t('unit(s)'))
                                                    : column.id === 'total' ? 
                                                        <span className='font-bold'>{new Intl.NumberFormat('en-US', loggedInUserCurrency ? {
                                                            style: 'currency',
                                                            currency: loggedInUserCurrency || '',
                                                        } : {}).format(
                                                            ((row?.unitPrice || 0) * (row?.quantity || 0)) - (((row?.unitPrice || 0) * (row?.quantity || 0)) * ((row?.discount || 0)/100))
                                                        )}</span>
                                                    : (row as any)[column.id]
                                                    }</TableCell>
                                            ))}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
                {po?.purchaseOrderSvc && po?.purchaseOrderSvc?.length ? <>
                    <Typography className="font-Saira text-[12px] font-semibold text-[#333843] text-left mb-2">{t('Additional Costs')}</Typography>
                    <Box className='w-[100%]' id="inventorytable">
                        <TableContainer>
                            <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size={'small'}>
                                <TableHead>
                                    <TableRow sx={{ borderBottom: `1px solid #B8BDCB` }}>
                                        {columnsService.filter((column) => column.id !== 'serviceId' && column.id !== 'serviceCode').map((column) => (
                                            <TableCell
                                                key={column.id}
                                                align={column.numeric ? 'right' : 'left'}
                                                padding={column.disablePadding ? 'none' : 'normal'}
                                                sortDirection={false}
                                                className="font-bold text-center"
                                            >{column.label}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody sx={{ '& tr:nth-child(odd)': { background: `transparent !important` }, '& tr': { borderBottom: `1px solid #B8BDCB` }, '& tr:last-child': { borderBottom: 0 } }}>
                                    {(po?.purchaseOrderSvc || []).map((row, index) => {
                                        const labelId = `enhanced-table-checkbox-${index}`;
                                        return (
                                            <TableRow hover tabIndex={-1} key={row.id} sx={{ height: '36px' }}>
                                                {columnsService.filter((column) => column.id !== 'serviceId' && column.id !== 'serviceCode').map((column) => (
                                                    <TableCell
                                                        key={column.id}
                                                        align={column.numeric ? 'right' : 'left'}
                                                        padding={column.disablePadding ? 'none' : 'normal'}
                                                        component={column.id === 'itemName' ? 'th' : undefined}
                                                        id={column.id === 'itemName' ? labelId : undefined}
                                                        scope={column.id === 'itemName' ? 'row' : undefined}
                                                        className="text-center"
                                                    >{column.id === 'total' ? 
                                                        <span className='font-bold'>{new Intl.NumberFormat('en-US', loggedInUserCurrency ? {
                                                            style: 'currency',
                                                            currency: loggedInUserCurrency || '',
                                                        } : {}).format(
                                                        ((row?.unitPrice || 0) * (row?.quantity || 0)) - (((row?.unitPrice || 0) * (row?.quantity || 0)) * ((row?.discount || 0)/100))
                                                    )}</span> : (row as any)[column.id]}</TableCell>
                                                ))}
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </> : null}
                <Box className={`flex ${po?.signature ? 'justify-between' : 'justify-end'} mt-4`}>
                    {po?.signature ? <Box>
                        <Typography className="font-Saira text-[12px] font-semibold text-[#171826] text-left">{t("signaturePlaceholder")}</Typography>
                        <div className="w-[270px] h-[120px] border rounded-[8px] border-[#999DAC] flex items-center justify-center overflow-hidden">
                            <img src={po?.signature} alt={t('Signature')} className="max-h-full max-w-full object-cover" />
                        </div>
                    </Box> : null}
                    <Box className="w-[360px]">
                        <Divider className="bg-[#AEB3C4] mb-2" />
                        <Box className='flex justify-between mb-[5px]'>
                            <Typography className="font-Saira text-[12px] text-left">
                                <span className="font-medium">{t('Requested')}: </span>
                                <span className="italic font-bold ms-1">{po?.totalQuantity || 0}</span>
                            </Typography>
                            <Typography className="font-Saira text-[12px] text-left">
                                <span className="font-medium">{t('Subtotal')}: </span>
                                <span className="italic font-bold ms-1">{new Intl.NumberFormat('en-US', {}).format((po as PurchaseOrderResponse)?.subtotalAmount || 0) || 0}</span>
                            </Typography>
                        </Box>
                        <Box className='flex justify-between mb-[5px]'>
                            <Typography className="font-Saira text-[12px] text-left">
                                <span className="font-medium">{t('Discount')}: </span>
                                <span className="italic font-bold ms-1">{new Intl.NumberFormat('en-US', {}).format((po as PurchaseOrderResponse)?.discount || 0) || 0}</span>
                            </Typography>
                            <Typography className="font-Saira text-[12px] text-left">
                                <span className="font-medium">{t('tax')}: </span>
                                <span className="italic font-bold ms-1">{new Intl.NumberFormat('en-US', {}).format((po as PurchaseOrderResponse)?.taxAmount || 0) || 0}</span>
                            </Typography>
                        </Box>
                        <Box className='flex justify-end mb-[5px]'>
                            <Typography className="font-Saira text-[12px] text-left">
                                <span className="font-medium">{t('totalWithTax')}: </span>
                                <span className="uppercase ms-1 font-semibold" style={{ color: themeColors?.primary }}>{loggedInUserCurrency}</span>
                                <span className="italic font-bold ms-1">{new Intl.NumberFormat('en-US', {}).format((po as PurchaseOrderResponse)?.totalAmount || 0) || 0}</span>
                            </Typography>
                        </Box>
                    </Box>
                </Box>
                {po?.poMemo ? <Box>
                    <Typography className="font-Saira text-[12px] font-semibold text-[#171826] text-left">{t("Purchase Order Memo")}</Typography>
                    <Typography className="font-Saira text-[12px] text-[#171826] text-left">{po?.poMemo}</Typography>
                </Box> : null}
            </Box>
        </Box>
    )
}

export default PrintPurchaseOrder;